import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Shield, 
  Store, 
  Users, 
  Package, 
  ShoppingCart,
  TrendingUp,
  Trash2,
  Edit,
  Key,
  Eye,
  Plus,
  LogOut,
  Globe,
  RefreshCw,
  Truck,
  AlertTriangle,
  Bell,
  Phone,
  Mail,
  Building2,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  LogIn,
  UserCheck
} from 'lucide-react';

const SuperAdminPage = () => {
  const { API_URL, getAuthHeader, user, logout, setUser, setToken } = useAuth();
  const { t, language, setLanguage, languageNames, languageFlags, availableLanguages } = useLanguage();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({});
  const [bakeries, setBakeries] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedBakery, setSelectedBakery] = useState(null);
  const [bakeryDetails, setBakeryDetails] = useState(null);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  
  // Dialogs
  const [addBakeryDialog, setAddBakeryDialog] = useState(false);
  const [editBakeryDialog, setEditBakeryDialog] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [registrationDialog, setRegistrationDialog] = useState(false);
  
  // Form states
  const [newBakery, setNewBakery] = useState({ 
    username: '', 
    password: '', 
    name: '',
    display_name: '',  // اسم اختياري للعرض
    manager_name: ''   // اسم المدير
  });
  const [editData, setEditData] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [userToDelete, setUserToDelete] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState('');
  const [registrationNotes, setRegistrationNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, bakeriesRes, usersRes, registrationsRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/superadmin/stats`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/admin/all-bakeries`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/superadmin/all-users`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/bakery-registrations`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/bakery-registrations/count`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setBakeries(bakeriesRes.data);
      setAllUsers(usersRes.data);
      setRegistrations(registrationsRes.data);
      setPendingCount(countRes.data.pending_count || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBakery = async () => {
    if (!newBakery.username || !newBakery.password || !newBakery.name) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/auth/register-bakery`, {
        username: newBakery.username,
        password: newBakery.password,
        name: newBakery.name,
        display_name: newBakery.display_name || newBakery.name,
        manager_name: newBakery.manager_name || '',
        role: 'admin'
      }, { headers: getAuthHeader() });
      toast.success('تم إضافة المخبز بنجاح');
      setAddBakeryDialog(false);
      setNewBakery({ username: '', password: '', name: '', display_name: '', manager_name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleUpdateBakery = async () => {
    try {
      await axios.put(`${API_URL}/api/superadmin/bakeries/${selectedBakery.id}`, editData, { headers: getAuthHeader() });
      toast.success('تم تحديث بيانات المخبز');
      setEditBakeryDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('كلمة المرور يجب أن تكون 4 أحرف على الأقل');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/superadmin/users/${selectedBakery.id}/password`, {
        new_password: newPassword
      }, { headers: getAuthHeader() });
      toast.success('تم تغيير كلمة المرور');
      setPasswordDialog(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleViewDetails = async (bakery) => {
    try {
      const res = await axios.get(`${API_URL}/api/superadmin/bakery/${bakery.id}/details`, { headers: getAuthHeader() });
      setBakeryDetails(res.data);
      setDetailsDialog(true);
    } catch (error) {
      toast.error('حدث خطأ في تحميل التفاصيل');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API_URL}/api/superadmin/users/${userToDelete.id}`, { headers: getAuthHeader() });
      toast.success('تم حذف المستخدم بنجاح');
      setDeleteDialog(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const openEditDialog = (bakery) => {
    setSelectedBakery(bakery);
    setEditData({
      name: bakery.name,
      username: bakery.username,
      company_name: bakery.company_info?.company_name || '',
      address: bakery.company_info?.address || '',
      phone: bakery.company_info?.phone || ''
    });
    setEditBakeryDialog(true);
  };

  const openPasswordDialog = (user) => {
    setSelectedBakery(user);
    setNewPassword('');
    setPasswordDialog(true);
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialog(true);
  };

  // Impersonation - Login as bakery
  const handleImpersonate = async (bakery) => {
    if (bakery.is_super_admin) {
      toast.error('لا يمكنك انتحال حساب المدير الرئيسي');
      return;
    }
    
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/impersonate/${bakery.id}`,
        {},
        { headers: getAuthHeader() }
      );
      
      // Store original admin info for return
      localStorage.setItem('original_admin_token', localStorage.getItem('token'));
      localStorage.setItem('original_admin_user', JSON.stringify(user));
      localStorage.setItem('is_impersonating', 'true');
      localStorage.setItem('impersonated_bakery_name', bakery.name);
      
      // Set new token and user
      const newToken = res.data.access_token;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(res.data.user);
      
      toast.success(`تم الدخول كـ "${bakery.name}"`);
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ في التبديل');
    }
  };

  // Registration handling functions
  const openRegistrationDialog = (registration) => {
    setSelectedRegistration(registration);
    setRegistrationStatus(registration.status);
    setRegistrationNotes(registration.notes || '');
    setRegistrationDialog(true);
  };

  const handleUpdateRegistration = async () => {
    try {
      await axios.put(
        `${API_URL}/api/bakery-registrations/${selectedRegistration.id}`,
        { status: registrationStatus, notes: registrationNotes },
        { headers: getAuthHeader() }
      );
      toast.success('تم تحديث حالة الطلب');
      setRegistrationDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleDeleteRegistration = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الطلب؟')) return;
    try {
      await axios.delete(`${API_URL}/api/bakery-registrations/${id}`, { headers: getAuthHeader() });
      toast.success('تم حذف الطلب');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'قيد الانتظار', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      contacted: { label: 'تم التواصل', color: 'bg-blue-100 text-blue-700', icon: MessageSquare },
      approved: { label: 'موافق عليه', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      rejected: { label: 'مرفوض', color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const LanguageSelector = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Globe className="w-4 h-4" />
          <span>{languageFlags[language]}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {availableLanguages.map((lang) => (
          <DropdownMenuItem 
            key={lang} 
            onClick={() => setLanguage(lang)}
            className={language === lang ? 'bg-red-50' : ''}
          >
            <span className="me-2">{languageFlags[lang]}</span>
            {languageNames[lang]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="animate-spin w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200" dir={language === 'ar' || language === 'ku' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-gradient-to-r from-red-700 to-red-600 text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10" />
            <div>
              <h1 className="text-2xl font-bold">لوحة الإدارة العامة</h1>
              <p className="text-red-100 text-sm">Super Admin Panel</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={fetchData} className="text-white hover:bg-red-800">
              <RefreshCw className="w-5 h-5" />
            </Button>
            <LanguageSelector />
            <Button variant="ghost" onClick={logout} className="text-white hover:bg-red-800">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4 text-center">
              <Store className="w-8 h-8 mx-auto mb-2" />
              <p className="text-3xl font-bold">{stats.total_bakeries || 0}</p>
              <p className="text-sm opacity-90">المخابز</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4 text-center">
              <Truck className="w-8 h-8 mx-auto mb-2" />
              <p className="text-3xl font-bold">{stats.total_drivers || 0}</p>
              <p className="text-sm opacity-90">السائقين</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 mx-auto mb-2" />
              <p className="text-3xl font-bold">{stats.total_customers || 0}</p>
              <p className="text-sm opacity-90">الزبائن</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4 text-center">
              <ShoppingCart className="w-8 h-8 mx-auto mb-2" />
              <p className="text-3xl font-bold">{stats.total_orders || 0}</p>
              <p className="text-sm opacity-90">الطلبات</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-pink-500 to-pink-600 text-white">
            <CardContent className="p-4 text-center">
              <Package className="w-8 h-8 mx-auto mb-2" />
              <p className="text-3xl font-bold">{stats.total_products || 0}</p>
              <p className="text-sm opacity-90">المنتجات</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white">
            <CardContent className="p-4 text-center">
              <ShoppingCart className="w-8 h-8 mx-auto mb-2" />
              <p className="text-3xl font-bold">{stats.today_orders || 0}</p>
              <p className="text-sm opacity-90">طلبات اليوم</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-2" />
              <p className="text-2xl font-bold">{(stats.total_revenue || 0).toFixed(0)}€</p>
              <p className="text-sm opacity-90">الإيرادات</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="registrations" className="space-y-6">
          <TabsList className="bg-white shadow-sm">
            <TabsTrigger value="registrations" className="data-[state=active]:bg-amber-100 relative">
              <Bell className="w-4 h-4 me-2" />
              طلبات التسجيل
              {pendingCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="bakeries" className="data-[state=active]:bg-red-100">
              <Store className="w-4 h-4 me-2" />
              إدارة المخابز
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-red-100">
              <Users className="w-4 h-4 me-2" />
              كل المستخدمين
            </TabsTrigger>
          </TabsList>

          {/* Registrations Tab */}
          <TabsContent value="registrations">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-600" />
                  طلبات تسجيل المخابز الجديدة ({registrations.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {registrations.length === 0 ? (
                  <div className="text-center py-12 text-stone-500">
                    <Bell className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">لا توجد طلبات تسجيل حالياً</p>
                    <p className="text-sm mt-2">ستظهر هنا طلبات المخابز الجديدة عند وصولها</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-right">#</TableHead>
                        <TableHead className="text-right">الاسم</TableHead>
                        <TableHead className="text-right">اسم المخبز</TableHead>
                        <TableHead className="text-right">الشركة</TableHead>
                        <TableHead className="text-right">الهاتف</TableHead>
                        <TableHead className="text-right">البريد</TableHead>
                        <TableHead className="text-center">الحالة</TableHead>
                        <TableHead className="text-center">التاريخ</TableHead>
                        <TableHead className="text-center">الإجراءات</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {registrations.map((reg, index) => (
                        <TableRow key={reg.id} className={reg.status === 'pending' ? 'bg-amber-50' : ''}>
                          <TableCell className="font-bold">{index + 1}</TableCell>
                          <TableCell className="font-medium">{reg.name}</TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1">
                              <Store className="w-4 h-4 text-amber-600" />
                              {reg.bakery_name}
                            </span>
                          </TableCell>
                          <TableCell className="text-stone-500">{reg.company_name || '-'}</TableCell>
                          <TableCell>
                            <a href={`tel:${reg.phone}`} className="flex items-center gap-1 text-blue-600 hover:underline" dir="ltr">
                              <Phone className="w-3 h-3" />
                              {reg.phone}
                            </a>
                          </TableCell>
                          <TableCell>
                            {reg.email ? (
                              <a href={`mailto:${reg.email}`} className="flex items-center gap-1 text-blue-600 hover:underline text-xs" dir="ltr">
                                <Mail className="w-3 h-3" />
                                {reg.email}
                              </a>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {getStatusBadge(reg.status)}
                          </TableCell>
                          <TableCell className="text-center text-sm text-stone-500">
                            {new Date(reg.created_at).toLocaleDateString('ar')}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openRegistrationDialog(reg)} title="تعديل الحالة">
                                <Edit className="w-4 h-4 text-amber-600" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteRegistration(reg.id)} title="حذف">
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bakeries Tab */}
          <TabsContent value="bakeries">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Store className="w-5 h-5 text-red-600" />
                  المخابز المسجلة ({bakeries.length})
                </CardTitle>
                <Button onClick={() => setAddBakeryDialog(true)} className="bg-red-600 hover:bg-red-700">
                  <Plus className="w-4 h-4 me-2" />
                  إضافة مخبز جديد
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-right">#</TableHead>
                      <TableHead className="text-right">اسم المخبز</TableHead>
                      <TableHead className="text-right">اسم المستخدم</TableHead>
                      <TableHead className="text-center">الزبائن</TableHead>
                      <TableHead className="text-center">الطلبات</TableHead>
                      <TableHead className="text-center">السائقين</TableHead>
                      <TableHead className="text-center">النوع</TableHead>
                      <TableHead className="text-center">الإجراءات</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bakeries.map((bakery, index) => (
                      <TableRow key={bakery.id}>
                        <TableCell className="font-bold">{index + 1}</TableCell>
                        <TableCell>
                          <p className="font-medium">{bakery.name}</p>
                          {bakery.company_info?.company_name && (
                            <p className="text-xs text-stone-500">{bakery.company_info.company_name}</p>
                          )}
                        </TableCell>
                        <TableCell className="font-mono">{bakery.username}</TableCell>
                        <TableCell className="text-center">
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm">
                            {bakery.customers_count}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm">
                            {bakery.orders_count}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-sm">
                            {bakery.drivers_count || 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {bakery.is_super_admin ? (
                            <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-bold">
                              مدير رئيسي
                            </span>
                          ) : (
                            <span className="bg-stone-100 text-stone-700 px-2 py-1 rounded-full text-xs">
                              مخبز
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            {!bakery.is_super_admin && (
                              <Button 
                                size="sm" 
                                variant="default" 
                                onClick={() => handleImpersonate(bakery)}
                                title="الدخول كـ هذا المخبز"
                                className="bg-green-600 hover:bg-green-700 text-white gap-1"
                              >
                                <LogIn className="w-4 h-4" />
                                دخول
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleViewDetails(bakery)}>
                              <Eye className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEditDialog(bakery)}>
                              <Edit className="w-4 h-4 text-amber-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openPasswordDialog(bakery)}>
                              <Key className="w-4 h-4 text-purple-600" />
                            </Button>
                            {!bakery.is_super_admin && (
                              <Button size="sm" variant="ghost" onClick={() => openDeleteDialog(bakery)}>
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-red-600" />
                  كل المستخدمين ({allUsers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-right">#</TableHead>
                      <TableHead className="text-right">الاسم</TableHead>
                      <TableHead className="text-right">اسم المستخدم</TableHead>
                      <TableHead className="text-center">الدور</TableHead>
                      <TableHead className="text-center">تاريخ الإنشاء</TableHead>
                      <TableHead className="text-center">الإجراءات</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allUsers.map((user, index) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-bold">{index + 1}</TableCell>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="font-mono">{user.username}</TableCell>
                        <TableCell className="text-center">
                          {user.is_super_admin ? (
                            <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-bold">
                              مدير رئيسي
                            </span>
                          ) : user.role === 'admin' ? (
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                              مدير مخبز
                            </span>
                          ) : (
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs">
                              سائق
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-sm text-stone-500">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString('ar') : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openPasswordDialog(user)}>
                              <Key className="w-4 h-4 text-purple-600" />
                            </Button>
                            {!user.is_super_admin && (
                              <Button size="sm" variant="ghost" onClick={() => openDeleteDialog(user)}>
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Bakery Dialog */}
      <Dialog open={addBakeryDialog} onOpenChange={setAddBakeryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-red-600" />
              إضافة مخبز جديد
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>اسم المخبز <span className="text-red-500">*</span></Label>
                <Input
                  value={newBakery.name}
                  onChange={(e) => setNewBakery({...newBakery, name: e.target.value})}
                  placeholder="مثال: مخبز الشام"
                />
              </div>
              <div className="space-y-2">
                <Label>الاسم المختصر (اختياري)</Label>
                <Input
                  value={newBakery.display_name}
                  onChange={(e) => setNewBakery({...newBakery, display_name: e.target.value})}
                  placeholder="مثال: الشام"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>اسم مدير المخبز (اختياري)</Label>
              <Input
                value={newBakery.manager_name}
                onChange={(e) => setNewBakery({...newBakery, manager_name: e.target.value})}
                placeholder="مثال: أحمد محمد"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>اسم المستخدم <span className="text-red-500">*</span></Label>
                <Input
                  value={newBakery.username}
                  onChange={(e) => setNewBakery({...newBakery, username: e.target.value})}
                  placeholder="اسم الدخول"
                  className="text-left"
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label>كلمة المرور <span className="text-red-500">*</span></Label>
                <Input
                  type="password"
                  value={newBakery.password}
                  onChange={(e) => setNewBakery({...newBakery, password: e.target.value})}
                  placeholder="كلمة المرور"
                  className="text-left"
                  dir="ltr"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddBakeryDialog(false)}>إلغاء</Button>
            <Button onClick={handleAddBakery} className="bg-red-600 hover:bg-red-700">
              <Plus className="w-4 h-4 me-2" />
              إضافة المخبز
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Bakery Dialog */}
      <Dialog open={editBakeryDialog} onOpenChange={setEditBakeryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              تعديل بيانات المخبز
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>اسم المخبز</Label>
                <Input
                  value={editData.name || ''}
                  onChange={(e) => setEditData({...editData, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>الاسم المختصر</Label>
                <Input
                  value={editData.display_name || ''}
                  onChange={(e) => setEditData({...editData, display_name: e.target.value})}
                  placeholder="اسم مختصر للعرض"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>اسم مدير المخبز</Label>
                <Input
                  value={editData.manager_name || ''}
                  onChange={(e) => setEditData({...editData, manager_name: e.target.value})}
                  placeholder="اسم المدير"
                />
              </div>
              <div className="space-y-2">
                <Label>اسم المستخدم</Label>
                <Input
                  value={editData.username || ''}
                  onChange={(e) => setEditData({...editData, username: e.target.value})}
                  className="text-left"
                  dir="ltr"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>اسم الشركة</Label>
              <Input
                value={editData.company_name || ''}
                onChange={(e) => setEditData({...editData, company_name: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>العنوان</Label>
                <Input
                  value={editData.address || ''}
                  onChange={(e) => setEditData({...editData, address: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>الهاتف</Label>
                <Input
                  value={editData.phone || ''}
                  onChange={(e) => setEditData({...editData, phone: e.target.value})}
                  className="text-left"
                  dir="ltr"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditBakeryDialog(false)}>إلغاء</Button>
            <Button onClick={handleUpdateBakery} className="bg-amber-600 hover:bg-amber-700">
              <CheckCircle className="w-4 h-4 me-2" />
              حفظ التغييرات
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>تغيير كلمة المرور</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-stone-600">تغيير كلمة المرور للمستخدم: <strong>{selectedBakery?.name}</strong></p>
            <div className="space-y-2">
              <Label>كلمة المرور الجديدة</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="كلمة المرور الجديدة"
                className="text-left"
                dir="ltr"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog(false)}>إلغاء</Button>
            <Button onClick={handleChangePassword} className="bg-purple-600 hover:bg-purple-700">تغيير</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bakery Details Dialog */}
      <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>تفاصيل المخبز: {bakeryDetails?.bakery?.name}</DialogTitle>
          </DialogHeader>
          {bakeryDetails && (
            <div className="space-y-6 py-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">{bakeryDetails.stats.customers_count}</p>
                  <p className="text-sm text-blue-600">الزبائن</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{bakeryDetails.stats.orders_count}</p>
                  <p className="text-sm text-green-600">الطلبات</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-700">{bakeryDetails.stats.total_revenue.toFixed(2)}€</p>
                  <p className="text-sm text-emerald-600">الإيرادات</p>
                </div>
              </div>

              {/* Products */}
              <div>
                <h4 className="font-bold mb-2">المنتجات ({bakeryDetails.products.length})</h4>
                <div className="flex flex-wrap gap-2">
                  {bakeryDetails.products.map(p => (
                    <span key={p.id} className="bg-stone-100 px-3 py-1 rounded-full text-sm">
                      {p.name} - {p.default_price}€
                    </span>
                  ))}
                </div>
              </div>

              {/* Drivers */}
              <div>
                <h4 className="font-bold mb-2">السائقين ({bakeryDetails.drivers.length})</h4>
                {bakeryDetails.drivers.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {bakeryDetails.drivers.map(d => (
                      <span key={d.id} className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                        {d.name} (@{d.username})
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-stone-500">لا يوجد سائقين</p>
                )}
              </div>

              {/* Recent Customers */}
              <div>
                <h4 className="font-bold mb-2">آخر الزبائن</h4>
                {bakeryDetails.customers.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {bakeryDetails.customers.slice(0, 10).map(c => (
                      <div key={c.id} className="bg-stone-50 p-2 rounded text-sm">
                        <p className="font-medium">{c.name}</p>
                        <p className="text-stone-500 text-xs">{c.address || 'بدون عنوان'}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-stone-500">لا يوجد زبائن</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              تأكيد الحذف
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-stone-700">
              هل أنت متأكد من حذف <strong>{userToDelete?.name}</strong>؟
            </p>
            {userToDelete?.role === 'admin' && (
              <p className="text-red-600 text-sm mt-2">
                ⚠️ سيتم حذف جميع بيانات المخبز (الزبائن، الطلبات، المنتجات، السائقين)
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>إلغاء</Button>
            <Button onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              <Trash2 className="w-4 h-4 me-2" />
              حذف نهائي
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Registration Status Dialog */}
      <Dialog open={registrationDialog} onOpenChange={setRegistrationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-600" />
              تفاصيل طلب التسجيل
            </DialogTitle>
          </DialogHeader>
          {selectedRegistration && (
            <div className="space-y-4 py-4">
              {/* Registration Info */}
              <div className="bg-stone-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-stone-500" />
                  <span className="font-medium">{selectedRegistration.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Store className="w-4 h-4 text-amber-600" />
                  <span>{selectedRegistration.bakery_name}</span>
                </div>
                {selectedRegistration.company_name && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-stone-500" />
                    <span>{selectedRegistration.company_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <a href={`tel:${selectedRegistration.phone}`} className="text-blue-600 hover:underline" dir="ltr">
                    {selectedRegistration.phone}
                  </a>
                </div>
                {selectedRegistration.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-stone-500" />
                    <a href={`mailto:${selectedRegistration.email}`} className="text-blue-600 hover:underline text-sm" dir="ltr">
                      {selectedRegistration.email}
                    </a>
                  </div>
                )}
                <div className="text-xs text-stone-500 pt-2 border-t">
                  تاريخ الطلب: {new Date(selectedRegistration.created_at).toLocaleString('ar')}
                </div>
              </div>

              {/* Status Select */}
              <div className="space-y-2">
                <Label>حالة الطلب</Label>
                <Select value={registrationStatus} onValueChange={setRegistrationStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر الحالة" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-yellow-600" />
                        قيد الانتظار
                      </span>
                    </SelectItem>
                    <SelectItem value="contacted">
                      <span className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-blue-600" />
                        تم التواصل
                      </span>
                    </SelectItem>
                    <SelectItem value="approved">
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        موافق عليه
                      </span>
                    </SelectItem>
                    <SelectItem value="rejected">
                      <span className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-600" />
                        مرفوض
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>ملاحظات</Label>
                <Textarea
                  value={registrationNotes}
                  onChange={(e) => setRegistrationNotes(e.target.value)}
                  placeholder="أضف ملاحظاتك هنا..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegistrationDialog(false)}>إلغاء</Button>
            <Button onClick={handleUpdateRegistration} className="bg-amber-600 hover:bg-amber-700">
              حفظ التغييرات
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPage;
